import React from "react";
import { motion as m } from "framer-motion";
import { useForm, ValidationError } from "@formspree/react";

const budgets = [
  "Under $15k",
  "$15k — $40k",
  "$40k — $80k",
  "$80k — $150k",
  "$150k +",
  "Retainer / ongoing",
  "Not sure yet",
];

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="t-eyebrow">{label}</span>
    {children}
    {hint && (
      <span className="mt-2 block text-xs text-ink-muted dark:text-slightwhite/50">
        {hint}
      </span>
    )}
  </label>
);

const inputClasses =
  "mt-3 w-full border-b border-[var(--color-border-strong)] bg-transparent pb-3 pt-1 text-[15px] outline-none transition-colors duration-300 placeholder:text-ink-muted/50 focus:border-azure dark:focus:border-electric";

const ContactForm = () => {
  const [state, handleSubmit] = useForm("xwkydgvq");

  if (state.succeeded) {
    return (
      <m.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex min-h-[420px] flex-col items-start justify-center rounded-lg border border-[var(--color-border)] bg-paper-warm p-10 dark:bg-night-surface"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-azure text-paper dark:bg-electric dark:text-paper">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="4 12 10 18 20 6" />
          </svg>
        </span>
        <h3 className="mt-7 font-display text-4xl leading-tight">
          Message received.
        </h3>
        <p className="t-lead mt-4 max-w-sm">
          Thank you — I read everything that comes through here and reply within
          two working days.
        </p>
      </m.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-[var(--color-border)] bg-paper-warm p-8 dark:bg-night-surface md:p-10"
    >
      <p className="t-eyebrow">Project enquiry</p>
      <h3 className="mt-4 font-display text-3xl leading-tight md:text-4xl">
        Tell me what you are building.
      </h3>

      <div className="mt-10 grid gap-8 sm:grid-cols-2">
        <Field label="Your name">
          <input
            type="text"
            required
            id="fname"
            name="name"
            placeholder="Ama Mensah"
            className={inputClasses}
          />
          <ValidationError prefix="Name" field="name" errors={state.errors} />
        </Field>

        <Field label="Email">
          <input
            type="email"
            required
            id="email"
            name="email"
            placeholder="you@company.com"
            className={inputClasses}
          />
          <ValidationError prefix="Email" field="email" errors={state.errors} />
        </Field>

        <Field label="Organisation">
          <input
            type="text"
            id="org"
            name="organisation"
            placeholder="Optional"
            className={inputClasses}
          />
        </Field>

        <Field label="Budget">
          <select id="budget" name="budget" className={`${inputClasses} cursor-pointer`}>
            {budgets.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-8">
        <Field
          label="The brief"
          hint="What is broken, who it affects, and what would count as success."
        >
          <textarea
            required
            id="message"
            name="message"
            rows="5"
            placeholder="We have a platform that…"
            className={`${inputClasses} resize-none`}
          />
          <ValidationError
            prefix="Message"
            field="message"
            errors={state.errors}
          />
        </Field>
      </div>

      <button
        type="submit"
        disabled={state.submitting}
        className="group mt-10 inline-flex w-full items-center justify-center gap-3 rounded-full bg-azure px-8 py-4 text-sm font-medium text-paper transition-all duration-300 hover:bg-azure-deep hover:shadow-glow disabled:opacity-50 dark:bg-electric dark:text-paper dark:hover:bg-electric-hover sm:w-auto"
      >
        {state.submitting ? "Sending…" : "Send the brief"}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="transition-transform duration-300 group-hover:translate-x-1"
        >
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </button>
    </form>
  );
};

export default ContactForm;
